import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Globe, 
  GraduationCap, 
  Briefcase, 
  Users, 
  Award, 
  CheckCircle, 
  Clock, 
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  BookOpen,
  Building,
  Heart,
  Stethoscope,
  Palmtree
} from 'lucide-react';

const J1Visa = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Thank you for your inquiry! We will contact you soon.');
    setFormData({ name: '', email: '', phone: '', program: '', message: '' });
  };

  const programs = [
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Student Intern',
      description: 'Gain practical work experience in your field of study while enrolled in a degree program abroad.',
      duration: '12 months',
      color: 'blue'
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: 'Trainee',
      description: 'Professional training program for recent graduates to enhance skills in their career field.',
      duration: '18 months',
      color: 'green'
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Teacher',
      description: 'Teaching positions in U.S. schools for qualified educators from abroad.',
      duration: '3 years',
      color: 'purple'
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: 'Work & Travel',
      description: 'Summer program for university students to work and travel in the United States.',
      duration: '4 months',
      color: 'orange'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Au Pair',
      description: 'Live with an American family and provide childcare while experiencing U.S. culture.',
      duration: '12 months',
      color: 'pink'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Camp Counselor',
      description: 'Work as a counselor at U.S. summer camps, leading activities and mentoring children.',
      duration: 'Summer',
      color: 'red'
    },
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: 'Research Scholar',
      description: 'Conduct research, observe, or consult in U.S. institutions and share findings.',
      duration: '5 years',
      color: 'indigo'
    },
    {
      icon: <Palmtree className="w-8 h-8" />,
      title: 'Summer Work Travel',
      description: 'Experience American culture while working seasonal jobs during university break.',
      duration: '4 months',
      color: 'teal'
    }
  ];

  const benefits = [
    {
      icon: <Globe className="w-6 h-6 text-blue-600" />,
      title: 'Cultural Exchange',
      description: 'Immerse yourself in American culture while sharing your own heritage'
    },
    {
      icon: <Award className="w-6 h-6 text-blue-600" />,
      title: 'Professional Growth',
      description: 'Gain valuable international work experience and enhance your resume'
    },
    {
      icon: <Users className="w-6 h-6 text-blue-600" />,
      title: 'Network Building',
      description: 'Connect with professionals and peers from around the world'
    },
    {
      icon: <BookOpen className="w-6 h-6 text-blue-600" />,
      title: 'Skill Development',
      description: 'Learn new skills and improve language proficiency in a real-world setting'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Choose Your Program',
      description: 'Select the J-1 visa category that matches your goals and qualifications'
    },
    {
      number: '2',
      title: 'Find a Sponsor',
      description: 'Work with a designated sponsor organization approved by the U.S. Department of State'
    },
    {
      number: '3',
      title: 'Complete DS-2019',
      description: 'Receive your Certificate of Eligibility for Exchange Visitor Status'
    },
    {
      number: '4',
      title: 'Pay SEVIS Fee',
      description: 'Submit the required SEVIS I-901 fee to the U.S. government'
    },
    {
      number: '5',
      title: 'Apply for Visa',
      description: 'Schedule and attend your visa interview at a U.S. embassy or consulate'
    },
    {
      number: '6',
      title: 'Begin Your Journey',
      description: 'Travel to the United States and start your exchange program'
    }
  ];

  const faqs = [
    {
      question: 'What is a J-1 Visa?',
      answer: 'The J-1 visa is a non-immigrant visa for individuals approved to participate in exchange visitor programs in the United States. It promotes cultural exchange and mutual understanding between Americans and people from other countries.'
    },
    {
      question: 'How long can I stay on a J-1 Visa?',
      answer: 'The duration varies by program category, ranging from a few weeks to several years. Most programs allow between 3 months to 18 months, with some categories permitting up to 5 years.'
    },
    {
      question: 'Can I work on a J-1 Visa?',
      answer: 'Yes, J-1 visa holders are authorized to work as specified in their program. The type and duration of employment depend on your specific J-1 category and program requirements.'
    },
    {
      question: 'Can I bring my family?',
      answer: 'J-1 visa holders may bring dependents (spouse and unmarried children under 21) on J-2 visas. J-2 dependents can apply for work authorization after arriving in the U.S.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Globe className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              J-1 Visa Exchange Programs
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Experience American culture, advance your career, and build international connections through official U.S. exchange visitor programs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => document.getElementById('programs').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Programs
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-blue-700"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose a J-1 Visa Program?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              J-1 visa programs offer unique opportunities for cultural exchange and professional development
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Programs Section */}
      <div id="programs" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              J-1 Visa Program Categories
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose from diverse exchange programs designed to match your career goals and interests
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((program, index) => (
              <Card 
                key={index} 
                className={`hover:shadow-xl transition-all cursor-pointer border-t-4 border-${program.color}-500`}
              >
                <CardContent className="pt-6">
                  <div className={`text-${program.color}-600 mb-4`}>
                    {program.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    {program.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    {program.description}
                  </p>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    Duration: {program.duration}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Application Process */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Application Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Follow these steps to begin your J-1 visa exchange program
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white text-xl font-bold">
                      {step.number}
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    {faq.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 ml-7">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div id="contact" className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl text-blue-100">
              Contact us today to learn more about J-1 visa programs
            </p>
          </div>
          
          <Card>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Program of Interest
                    </label>
                    <select
                      value={formData.program}
                      onChange={(e) => setFormData({...formData, program: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a program</option>
                      {programs.map((prog, idx) => (
                        <option key={idx} value={prog.title}>{prog.title}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Tell us about your goals and any questions you have..."
                  />
                </div>

                <Button type="submit" className="w-full" size="lg">
                  Submit Inquiry
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div>
                    <Mail className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-sm font-medium text-gray-900">Email</p>
                    <p className="text-sm text-gray-600">info@digidreamsolutions.com</p>
                  </div>
                  <div>
                    <Phone className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">+1 (808) 756-2017</p>
                  </div>
                  <div>
                    <MapPin className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-sm font-medium text-gray-900">Location</p>
                    <p className="text-sm text-gray-600">Hawaii, USA</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer Disclaimer */}
      <div className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm">
          <p className="mb-2">
            <strong className="text-white">Important:</strong> This website provides general information about J-1 visa exchange programs. 
            For official program details and application requirements, please visit{' '}
            <a href="https://j1visa.state.gov" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
              j1visa.state.gov
            </a>
          </p>
          <p className="text-xs">
            All J-1 visa applications must be processed through designated sponsor organizations approved by the U.S. Department of State.
          </p>
        </div>
      </div>
    </div>
  );
};

export default J1Visa;
